import { type FC, Fragment } from "react";
import { Table } from "@root/shared/components/Table/legacy/Table";
import { DetailTableRow } from "@clientFrontend/features/publication/components/DetailTableRow";
import { LazyLoadImage } from "@root/shared/components/LazyLoadImage";
import { Link } from "@root/shared/components/Link";
import { Icon } from "@root/shared/components/Icon";
import { MultiLineText } from "@root/shared/components/MultiLineText";
import { MODEL_ATTRIBUTE_NAME } from "@root/shared/utils/constants";
import type { WorkContent } from "./type";
import * as styles from "./styles.css";

type Props = {
  editUrl: string;
  canEdit: boolean;
  workContent: WorkContent;
  dummyImgPath: string;
  site: "biyo" | "kaigo";
};

export const WorkContentDetail: FC<Props> = ({
  editUrl,
  canEdit,
  workContent,
  dummyImgPath,
  site,
}) => {
  return (
    <>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>求人情報</h2>
        <Table>
          <DetailTableRow header="求人タイトル">
            {workContent.title || "文字数40文字以内でご記入ください"}
          </DetailTableRow>
          <DetailTableRow header="職種">{workContent.businessType}</DetailTableRow>
          <DetailTableRow header={MODEL_ATTRIBUTE_NAME.workContent.occupation}>
            {workContent.occupation}
          </DetailTableRow>
          <DetailTableRow header={MODEL_ATTRIBUTE_NAME.workContent.occupationText}>
            {workContent.occupationText && <MultiLineText text={workContent.occupationText} />}
          </DetailTableRow>
          <DetailTableRow header="画像">
            <div className={styles.basicImages}>
              {workContent.basicImages?.map((item, index) => (
                <div key={item.id}>
                  <figure className={styles.imageContainer}>
                    <figcaption>{index + 1}枚目</figcaption>
                    <LazyLoadImage
                      src={item.src}
                      alt={`画像(${index + 1}枚目)`}
                      dummyImgPath={dummyImgPath}
                      width={280}
                      height={158}
                    />
                    <figcaption>カテゴリ名: {item.categoryName}</figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </DetailTableRow>
          <DetailTableRow header="検索一覧用画像">
            {workContent.searchImage && (
              <figure className={styles.imageContainer}>
                <LazyLoadImage
                  src={workContent.searchImage.src}
                  alt="検索一覧用画像"
                  dummyImgPath={dummyImgPath}
                  width={280}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
          <DetailTableRow header="SNSシェアなどに利用される画像">
            {workContent.shareImage && (
              <figure className={styles.imageContainer}>
                <LazyLoadImage
                  src={workContent.shareImage.src}
                  alt="SNSシェアなどに利用される画像"
                  dummyImgPath={dummyImgPath}
                  width={280}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
        </Table>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>想い・こだわり</h2>
        <Table>
          <DetailTableRow header="企業画像">
            {workContent.workClientImage && (
              <figure className={styles.imageContainer}>
                <LazyLoadImage
                  src={workContent.workClientImage.src}
                  alt="企業画像"
                  dummyImgPath={dummyImgPath}
                  width={280}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
          <DetailTableRow
            header={workContent.dreamType ? `${workContent.dreamType}の夢・想い` : "夢・想い"}
          >
            {workContent.dreamText && <MultiLineText text={workContent.dreamText} />}
          </DetailTableRow>
          <DetailTableRow header="こだわり・自慢">
            {workContent.commitments.map((item) => (
              <Fragment key={`${item.commitmentType}_${item.commitmentText}`}>
                <p className={styles.commitmentTitle}>{item.commitmentType}</p>
                <div className={styles.commitmentText}>
                  {item.commitmentText && <MultiLineText text={item.commitmentText} />}
                </div>
              </Fragment>
            ))}
          </DetailTableRow>
          <DetailTableRow header="メッセージの顔写真">
            {workContent.messageImage && (
              <figure className={styles.imageContainer}>
                <LazyLoadImage
                  src={workContent.messageImage.src}
                  alt="メッセージの顔写真"
                  dummyImgPath={dummyImgPath}
                  width={280}
                  height={158}
                />
              </figure>
            )}
          </DetailTableRow>
          <DetailTableRow
            header={
              workContent.messageFrom ? `${workContent.messageFrom}からのメッセージ` : "メッセージ"
            }
          >
            {workContent.message && <MultiLineText text={workContent.message} />}
          </DetailTableRow>
        </Table>
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>求める人物像</h2>
        {workContent.hopeCharacterText && <MultiLineText text={workContent.hopeCharacterText} />}
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>PR</h2>
        {workContent.prText && <MultiLineText text={workContent.prText} />}
      </section>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle[site]}>応募と今後の流れ</h2>
        {workContent.recruitmentStep && <MultiLineText text={workContent.recruitmentStep} />}
      </section>
      <div className={styles.editButtonContainer}>
        <Link href={editUrl} theme={canEdit ? "primary" : "disabled"} className={styles.editButton}>
          <Icon name="edit" />
          編集する
        </Link>
      </div>
    </>
  );
};
